/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from './ThemedLoadingSpinner';
import EmployerFilterDropdown from './EmployerFilterDropdown';
import '../styles/InterviewCalendar.css';

const CACHE_TTL_MS = 2 * 60 * 1000;

const INTERVIEW_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All interviews' },
  { value: 'job', label: 'Job interviews' },
  { value: 'internship', label: 'Intern interviews' },
];

const INTERVIEW_MODE_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-Person' },
  { value: 'phone', label: 'Phone' },
];

let cachedInterviews = null;
let cachedApplications = null;
let calendarCacheTimestamp = 0;
let cachedTokenSignature = null;

const InterviewCalendar = ({ variant = 'recruiter', onStatsChange, onUpcomingChange, viewUserId, isMobileView = false, useCustomDropdown = false }) => {
  const shouldUseCustomDropdown = useCustomDropdown;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [interviews, setInterviews] = useState([]);
  const [filteredInterviews, setFilteredInterviews] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const isEmployerView = variant === 'employer';
  const isInternView = variant === 'intern';
  const isCandidateView = variant === 'jobseeker' || isInternView;
  const [interviewTypeFilter, setInterviewTypeFilter] = useState('all');
  const [calendlySettings, setCalendlySettings] = useState({ useCalendly: false, calendlyUrl: '' });
  const [calendlyScriptLoaded, setCalendlyScriptLoaded] = useState(false);

  const getTokenSignature = () => {
    const token = localStorage.getItem('token');
    return token ? token.slice(-12) : null;
  };

  const clearCache = () => {
    cachedInterviews = null;
    cachedApplications = null;
    calendarCacheTimestamp = 0;
    cachedTokenSignature = null;
  };

  const updateCache = (
    interviewsData,
    applicationsData,
    timestamp = Date.now(),
    tokenSignature = cachedTokenSignature ?? getTokenSignature()
  ) => {
    if (Array.isArray(interviewsData)) {
      cachedInterviews = interviewsData;
    }
    if (Array.isArray(applicationsData)) {
      cachedApplications = applicationsData;
    }
    calendarCacheTimestamp = timestamp;
    cachedTokenSignature = tokenSignature;
  };

  const buildCalendarStats = (list = []) => {
    const interviewsList = Array.isArray(list) ? list.filter(Boolean) : [];
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    const totalScheduled = interviewsList.length;
    const thisMonth = interviewsList.filter((interview) => {
      const interviewDate =
        interview?.interview_local_date ||
        parseDateKey(interview?.interview_date);

      return (
        interviewDate instanceof Date &&
        !Number.isNaN(interviewDate.getTime()) &&
        interviewDate >= monthStart &&
        interviewDate <= monthEnd
      );
    }).length;

    const upcomingCount = interviewsList.filter((interview) => {
      const interviewDateTime = getInterviewDateTime(interview);
      return interviewDateTime && interviewDateTime >= now;
    }).length;

    return {
      totalScheduled,
      thisMonth,
      upcoming: upcomingCount
    };
  };

  const selectUpcomingInterviews = (list = [], limit = 5) => {
    const interviewsList = Array.isArray(list) ? list.filter(Boolean) : [];
    const now = new Date();

    return interviewsList
      .filter((interview) => {
        const interviewDateTime = getInterviewDateTime(interview);
        return interviewDateTime && interviewDateTime >= now;
      })
      .sort((a, b) => getInterviewDateTime(a) - getInterviewDateTime(b))
      .slice(0, limit);
  };

  const emitCalendarUpdates = (list = []) => {
    if (!onStatsChange && !onUpcomingChange) return;

    const safeList = Array.isArray(list) ? list.filter(Boolean) : [];

    if (onStatsChange) {
      onStatsChange(buildCalendarStats(safeList));
    }
    if (onUpcomingChange) {
      onUpcomingChange(selectUpcomingInterviews(safeList));
    }
  };

  const applyCachedDataIfAvailable = () => {
    const tokenSignature = getTokenSignature();

    if (
      cachedTokenSignature &&
      tokenSignature &&
      cachedTokenSignature !== tokenSignature
    ) {
      clearCache();
      return { usedCache: false, isStale: true };
    }

    const hasInterviewsCache = Array.isArray(cachedInterviews);
    const hasApplicationsCache = Array.isArray(cachedApplications);

    if (!hasInterviewsCache && !hasApplicationsCache) {
      return { usedCache: false, isStale: true };
    }

    let normalizedInterviews = null;

    if (hasInterviewsCache) {
      normalizedInterviews = cachedInterviews.map(transformInterview).filter(Boolean);
      setInterviews(normalizedInterviews);
    }
    if (hasApplicationsCache) {
      setApplications(cachedApplications.map(transformApplication).filter(Boolean));
    }
    setLoading(false);

    const listForUpdates = normalizedInterviews ?? interviews;
    emitCalendarUpdates(listForUpdates);

    const isStale =
      !calendarCacheTimestamp ||
      Date.now() - calendarCacheTimestamp > CACHE_TTL_MS;

    return { usedCache: true, isStale };
  };
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    applicant_id: '',
    job_id: '',
    application_id: '',
    interview_type: 'job',
    interview_date: '',
    interview_start_time: '',
    interview_end_time: '',
    interview_mode: 'online',
    interview_link: '',
    interview_location: '',
    interview_notes: ''
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    interview_date: '',
    interview_start_time: '',
    interview_end_time: '',
    interview_mode: 'online',
    interview_link: '',
    interview_location: '',
    interview_notes: ''
  });

  const applicationSelectOptions = useMemo(() => ([
    { value: '', label: 'Choose an application...' },
    ...applications.map((app, idx) => ({
      value: `${app.applicant_id}|${app.job_id}|${app.interview_type}|${app._id || ''}`,
      label: `${app.applicant_name} · ${app.job_title} (${app.interview_type === 'internship' ? 'Intern' : 'Job'})`,
      key: idx,
    })),
  ]), [applications]);

  const scheduleApplicationValue = scheduleForm.applicant_id
    ? `${scheduleForm.applicant_id}|${scheduleForm.job_id}|${scheduleForm.interview_type}|${scheduleForm.application_id || ''}`
    : '';

  const handleScheduleApplicationChange = (selectedValue) => {
    if (!selectedValue) {
      setScheduleForm((prev) => ({
        ...prev,
        applicant_id: '',
        job_id: '',
        interview_type: '',
        application_id: '',
      }));
      return;
    }

    const [applicant_id, job_id, interview_type, application_id] = selectedValue.split('|');
    setScheduleForm((prev) => ({
      ...prev,
      applicant_id,
      job_id,
      interview_type,
      application_id,
    }));
  };

  const renderInterviewModeSelect = (currentValue, onValueChange) => (
    shouldUseCustomDropdown ? (
      <EmployerFilterDropdown
        showCounts={false}
        value={currentValue}
        onChange={onValueChange}
        options={INTERVIEW_MODE_OPTIONS}
      />
    ) : (
      <select
        value={currentValue}
        onChange={(e) => onValueChange(e.target.value)}
        required
      >
        {INTERVIEW_MODE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    )
  );

  const renderApplicationSelect = (required = true) => (
    shouldUseCustomDropdown ? (
      <EmployerFilterDropdown
        showCounts={false}
        value={scheduleApplicationValue}
        onChange={handleScheduleApplicationChange}
        options={applicationSelectOptions}
      />
    ) : (
      <select
        value={scheduleApplicationValue}
        onChange={(e) => handleScheduleApplicationChange(e.target.value)}
        required={required}
      >
        {applicationSelectOptions.map((option, idx) => (
          <option key={option.key ?? option.value ?? idx} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )
  );

  const formatDateKey = (dateObj) => {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDateKey = (dateString) => {
    if (!dateString) return null;
    const [datePart] = dateString.split('T');
    if (!datePart) return null;
    const [year, month, day] = datePart.split('-').map(Number);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
    return new Date(year, month - 1, day);
  };

  const createDateTime = (dateKey, timeString) => {
    const base = parseDateKey(dateKey);
    if (!base) return null;
    if (!timeString) return base;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (Number.isNaN(hours)) return base;
    return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes || 0);
  };

  const transformInterview = (interview) => {
    if (!interview) return null;
    const type = normalizeInterviewType(interview);
    const dateKey = interview.interview_date ? interview.interview_date.split('T')[0] : '';
    const startTime = interview.interview_start_time || interview.interview_time || '';
    const endTime = interview.interview_end_time || '';
    const localDate = parseDateKey(dateKey);
    return {
      ...interview,
      interview_type: type,
      interview_date_key: dateKey,
      interview_local_date: localDate,
      interview_start_time: startTime,
      interview_end_time: endTime,
      interview_time: startTime,
      interview_local_start: createDateTime(dateKey, startTime),
      job_title: interview.job_title || interview.internship_title || interview.role_title || 'Interview',
      applicant_name: interview.applicant_name || interview.candidate_name || 'Candidate',
      interview_mode: interview.interview_mode || 'online'
    };
  };

  const transformApplication = (app) => {
    if (!app) return null;
    const applicantId =
      app.applicant_id ||
      app.userId ||
      app.applicantId ||
      app.candidate_id ||
      app.candidateId;
    const jobId =
      app.job_id ||
      app.jobId ||
      app.job?._id ||
      app.jobIdRef;
    const internshipId =
      app.internship_id ||
      app.internshipId ||
      app.internship?._id;
    const interviewType = internshipId ? 'internship' : 'job';
    const dateKey = app.interview_date ? app.interview_date.split('T')[0] : '';
    const startTime = app.interview_start_time || app.interview_time || '';

    return {
      ...app,
      applicant_id: applicantId ? String(applicantId) : '',
      job_id: internshipId ? String(internshipId) : jobId ? String(jobId) : '',
      interview_type: interviewType,
      interview_date_key: dateKey,
      interview_start_time: startTime,
      interview_end_time: app.interview_end_time || '',
      applicant_name: app.candidate_name || app.applicant_name || app.applicant?.name || 'Candidate',
      job_title: interviewType === 'internship'
        ? app.internship_title || app.job_title || 'Internship'
        : app.job_title || app.position || 'Job'
    };
  };

  const normalizeInterviewType = (interview) => {
    const type = interview?.interview_type || interview?.interviewType || interview?.type;
    return type === 'internship' ? 'internship' : 'job';
  };

  const activeInterviews = useMemo(() => {
    if (interviewTypeFilter === 'all') {
      return interviews;
    }
    return interviews.filter((interview) => normalizeInterviewType(interview) === interviewTypeFilter);
  }, [interviews, interviewTypeFilter]);

  const formatTimeValue = (value) => {
    if (!value) return '';
    if (!value.includes(':')) {
      return value;
    }
    const time = new Date(`1970-01-01T${value}`);
    if (Number.isNaN(time.getTime())) {
      return value;
    }
    return time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatTimeRange = (start, end) => {
    if (!start && !end) {
      return 'Time TBD';
    }
    const formattedStart = formatTimeValue(start);
    const formattedEnd = formatTimeValue(end);
    return formattedEnd ? `${formattedStart} - ${formattedEnd}` : formattedStart || formattedEnd;
  };

  const getInterviewDateTime = (interview) => {
    if (interview?.interview_local_start instanceof Date && !Number.isNaN(interview.interview_local_start.getTime())) {
      return interview.interview_local_start;
    }
    const dateKey = interview?.interview_date_key || (interview?.interview_date ? interview.interview_date.split('T')[0] : '');
    const fallback = createDateTime(dateKey, interview?.interview_start_time || interview?.interview_time);
    return fallback || new Date(0);
  };

  const getDisplayTime = (interview) => {
    return formatTimeRange(
      interview?.interview_start_time || interview?.interview_time || '',
      interview?.interview_end_time || ''
    );
  };

  useEffect(() => {
    const handleInterviewScheduled = () => {
      fetchCalendarData({ showSpinner: false });
    };

    window.addEventListener('interviewScheduled', handleInterviewScheduled);
    return () => window.removeEventListener('interviewScheduled', handleInterviewScheduled);
  }, []);

  // Load Calendly settings and script
  useEffect(() => {
    const fetchCalendlySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Use the correct backend endpoint for recruiter profile
        // Backend registers recruiter routes under /api/recruiters/*
        const response = await fetch(buildApiUrl('/api/recruiters/profile'), {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setCalendlySettings({
            useCalendly: data.useCalendly === true,
            calendlyUrl: data.calendlyUrl || ''
          });
        }
      } catch (error) {
        console.error('Error fetching Calendly settings:', error);
      }
    };

    fetchCalendlySettings();

    // Load Calendly script
    if (!calendlyScriptLoaded && !document.querySelector('script[src*="calendly.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => setCalendlyScriptLoaded(true);
      document.body.appendChild(script);
    } else if (document.querySelector('script[src*="calendly.com"]')) {
      setCalendlyScriptLoaded(true);
    }
  }, [calendlyScriptLoaded]);

  useEffect(() => {
    // Filter interviews for the current month + type
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    const filtered = activeInterviews.filter(interview => {
      const interviewDate = interview.interview_local_date || parseDateKey(interview.interview_date);
      return interviewDate && interviewDate >= monthStart && interviewDate <= monthEnd;
    });
    
    setFilteredInterviews(filtered);
  }, [currentDate, activeInterviews]);

  const fetchCalendarData = async ({
    fetchInterviews: shouldFetchInterviews = true,
    fetchApplications: shouldFetchApplications = true,
    showSpinner = true
  } = {}) => {
    if (!shouldFetchInterviews && !shouldFetchApplications) {
      return;
    }

      const token = localStorage.getItem('token');
    const tokenSignature = getTokenSignature();

    if (!token) {
      setLoading(false);
      emitCalendarUpdates(interviews);
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };
    let updatedInterviews = interviews;
    let updatedApplications = applications;

    if (showSpinner) {
      setLoading(true);
    }

    try {
      const fetchPromises = [];

      if (shouldFetchInterviews) {
        const interviewsUrl = isCandidateView
          ? buildApiUrl('/api/applications/candidate/interviews')
          : (viewUserId
            ? buildApiUrl(`/api/applications/recruiter/interviews?userId=${viewUserId}`)
            : buildApiUrl('/api/applications/recruiter/interviews'));

        fetchPromises.push(
          fetch(interviewsUrl, { headers })
            .then(async (response) => {
              if (response.ok) {
                const data = await response.json();
                const mapped = (data.interviews || []).map(transformInterview).filter(Boolean);
                updatedInterviews = mapped;
                setInterviews(mapped);
                return { success: true, type: 'interviews' };
              } else {
                console.error('Failed to fetch interviews:', response.status, response.statusText);
                return { success: false, type: 'interviews' };
              }
            })
            .catch((error) => {
              console.error('Error fetching interviews:', error);
              return { success: false, type: 'interviews', error };
            })
        );
      }

      if (shouldFetchApplications && !isCandidateView) {
        const applicationsUrl = viewUserId
          ? buildApiUrl(`/api/recruiters/applications?userId=${viewUserId}`)
          : buildApiUrl('/api/recruiters/applications');

        fetchPromises.push(
          fetch(applicationsUrl, { headers })
            .then(async (response) => {
              if (response.ok) {
                const data = await response.json();
                const eligibleApps = Array.isArray(data)
                  ? data.filter(app => ['shortlisted', 'reviewed', 'pending'].includes(app.status))
                  : [];
                const mappedApps = eligibleApps.map(transformApplication).filter(Boolean);
                updatedApplications = mappedApps;
                setApplications(mappedApps);
                return { success: true, type: 'applications' };
              } else {
                console.error('Failed to fetch applications:', response.status, response.statusText);
                return { success: false, type: 'applications' };
              }
            })
            .catch((error) => {
              console.error('Error fetching applications:', error);
              return { success: false, type: 'applications', error };
            })
        );
      }

      await Promise.all(fetchPromises);

      const interviewsForCache = shouldFetchInterviews ? updatedInterviews : cachedInterviews || interviews;
      const applicationsForCache = shouldFetchApplications ? updatedApplications : cachedApplications || applications;
      updateCache(interviewsForCache, applicationsForCache, Date.now(), tokenSignature);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
      const latestInterviews = shouldFetchInterviews ? updatedInterviews : interviews;
      emitCalendarUpdates(latestInterviews);
    }
  };

  useEffect(() => {
    if (isCandidateView) {
      fetchCalendarData({ showSpinner: true, fetchApplications: false });
      return;
    }
    const { usedCache, isStale } = applyCachedDataIfAvailable();

    if (!usedCache) {
      fetchCalendarData({ showSpinner: true });
    } else if (isStale) {
      fetchCalendarData({ showSpinner: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId, isCandidateView]); // Reload when viewUserId or variant changes

  useEffect(() => {
    if (showScheduleModal) {
      const today = new Date();
      const dateStr = formatDateKey(today);
      setScheduleForm((prev) => ({
        ...prev,
        interview_date: dateStr,
        interview_start_time: '10:00',
        interview_end_time: '10:30'
      }));
    }
  }, [showScheduleModal]);

  const handleScheduleInterview = async (e) => {
    e.preventDefault();

    if (scheduleForm.interview_mode === 'online' && (!scheduleForm.interview_link || !scheduleForm.interview_link.trim())) {
      alert('Meeting Link is required when Interview Mode is Online. Please enter a meeting link to schedule the interview.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/applications/schedule-interview'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(scheduleForm)
      });

      if (response.ok) {
        alert('Interview scheduled successfully!');
        setShowScheduleModal(false);
        resetScheduleForm();
        fetchCalendarData({ showSpinner: false });
      } else {
        const error = await response.json();
        alert(`Failed to schedule interview: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error scheduling interview:', error);
      alert('Error scheduling interview. Please try again.');
    }
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      applicant_id: '',
      job_id: '',
      application_id: '',
      interview_type: 'job',
      interview_date: '',
      interview_start_time: '',
      interview_end_time: '',
      interview_mode: 'online',
      interview_link: '',
      interview_location: '',
      interview_notes: ''
    });
  };

  const initializeEditForm = (interview) => {
    if (!interview) return;
    
    const dateKey = interview.interview_date ? interview.interview_date.split('T')[0] : '';
    const startTime = interview.interview_start_time || interview.interview_time || '';
    const endTime = interview.interview_end_time || '';
    
    setEditForm({
      interview_date: dateKey,
      interview_start_time: startTime,
      interview_end_time: endTime,
      interview_mode: interview.interview_mode || 'online',
      interview_link: interview.interview_link || '',
      interview_location: interview.interview_location || '',
      interview_notes: interview.interview_notes || interview.notes || ''
    });
  };

  const handleEditInterview = async (e) => {
    e.preventDefault();
    
    if (!selectedInterview) return;
    
    // Find the application_id from the interview
    const applicationId = selectedInterview._id || selectedInterview.application_id || selectedInterview.id;
    
    if (!applicationId) {
      alert('Unable to find application ID. Please try again.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Format the interview date (can be date only or datetime)
      const interviewDateTime = editForm.interview_date && editForm.interview_start_time
        ? `${editForm.interview_date}T${editForm.interview_start_time}:00`
        : editForm.interview_date || null;
      
      const interviewMode = editForm.interview_mode || 'online';
      const normalizedLink = interviewMode === 'online' ? (editForm.interview_link || '') : '';
      const normalizedLocation = interviewMode === 'in-person' ? (editForm.interview_location || '') : '';
      const normalizedNotes = editForm.interview_notes ?? '';

      const updateData = {
        status: 'interview_scheduled', // Keep the status as interview_scheduled
        interview_date: interviewDateTime,
        interview_mode: interviewMode,
        interview_start_time: editForm.interview_start_time ?? '',
        interview_end_time: editForm.interview_end_time ?? '',
        interview_link: normalizedLink,
        interview_location: normalizedLocation,
        interview_notes: normalizedNotes,
        // Some parts of the backend/UI also use `notes`
        notes: normalizedNotes
      };
      
      const response = await fetch(buildApiUrl(`/api/recruiters/applications/${applicationId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('Interview updated successfully!');
        setIsEditMode(false);
        setShowInterviewModal(false);
        fetchCalendarData({ showSpinner: false });
      } else {
        const error = await response.json();
        alert(`Failed to update interview: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating interview:', error);
      alert('Error updating interview. Please try again.');
    }
  };

  const handleDeleteInterview = async () => {
    if (!selectedInterview) return;

    const applicationId = selectedInterview._id || selectedInterview.application_id || selectedInterview.id;
    if (!applicationId) {
      alert('Unable to find application ID. Please try again.');
      return;
    }

    const confirmed = window.confirm('Delete this interview? This will remove it from the calendar.');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');

      // Revert status and clear interview fields so it no longer appears in calendar.
      const updateData = {
        status: 'pending',
        interview_date: '',
        interview_start_time: '',
        interview_end_time: '',
        interview_mode: '',
        interview_link: '',
        interview_location: '',
        interview_notes: '',
        notes: ''
      };

      const response = await fetch(buildApiUrl(`/api/recruiters/applications/${applicationId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('Interview deleted successfully.');
        setIsEditMode(false);
        setShowInterviewModal(false);
        fetchCalendarData({ showSpinner: false });
      } else {
        const error = await response.json();
        alert(`Failed to delete interview: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting interview:', error);
      alert('Error deleting interview. Please try again.');
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getInterviewsForDate = (day) => {
    const targetKey = formatDateKey(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    ));
    
    return filteredInterviews.filter(interview => 
      (interview.interview_date_key ||
       (interview.interview_date ? interview.interview_date.split('T')[0] : '')) === targetKey
    );
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (dateString) => {
    const explicitDate = parseDateKey(dateString);
    const date = explicitDate || (dateString ? new Date(dateString) : null);
    if (!date || Number.isNaN(date.getTime())) return 'Date not set';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    const formatted = formatTimeValue(timeString);
    return formatted || 'Time not set';
  };

  const getUpcomingInterviews = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return activeInterviews
      .filter(interview => {
        const interviewDate = interview.interview_local_date || parseDateKey(interview.interview_date);
        return interviewDate && interviewDate >= today;
      })
      .sort((a, b) => getInterviewDateTime(a) - getInterviewDateTime(b))
      .slice(0, 5);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    setSelectedDate(clickedDate);
    const dayInterviews = getInterviewsForDate(day);
    
    if (dayInterviews.length === 1) {
      setSelectedInterview(dayInterviews[0]);
      setShowInterviewModal(true);
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Week day headers
    const headers = weekDays.map(day => (
      <div key={day} className="calendar-header-day">
        {day}
      </div>
    ));

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      currentDay.setHours(0, 0, 0, 0);
      
      const dayInterviews = getInterviewsForDate(day);
      const isToday = currentDay.getTime() === today.getTime();
      const isPast = currentDay < today;
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${isPast ? 'past' : ''} ${dayInterviews.length > 0 ? 'has-interviews' : ''}`}
          onClick={() => handleDateClick(day)}
          onMouseEnter={() => dayInterviews.length > 0 && setHoveredDay(day)}
          onMouseLeave={() => setHoveredDay(null)}
        >
          <div className="day-number">{day}</div>
          {dayInterviews.length > 0 && hoveredDay === day && (
            <div className="calendar-day-tooltip">
              {dayInterviews.map((interview, idx) => (
                <div key={idx} className="tooltip-interview-item">
                  <div className="tooltip-interview-header">
                    <span className={`interview-type-badge ${normalizeInterviewType(interview)}`}>
                      {normalizeInterviewType(interview) === 'internship' ? 'Intern' : 'Job'}
                    </span>
                    <span className="tooltip-time">{getDisplayTime(interview)}</span>
                  </div>
                  <div className="tooltip-candidate">{interview.applicant_name}</div>
                  <div className="tooltip-role">{interview.job_title}</div>
                  <div className="tooltip-mode">
                    <i className={`fas ${interview.interview_mode === 'online' ? 'fa-video' : interview.interview_mode === 'in-person' ? 'fa-map-marker-alt' : 'fa-phone'}`} style={{ marginRight: 4, opacity: 0.7 }} />
                    {interview.interview_mode}
                    {interview.interview_mode === 'in-person' && interview.interview_location && (
                      <span className="tooltip-location"> · {interview.interview_location}</span>
                    )}
                  </div>
                  {interview.interview_mode === 'online' && interview.interview_link && (
                    <a
                      href={interview.interview_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tooltip-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="fas fa-external-link-alt" /> Meeting link
                    </a>
                  )}
                  {idx < dayInterviews.length - 1 && <hr className="tooltip-divider" />}
                </div>
              ))}
              <div className="tooltip-hint">Click to view full details</div>
            </div>
          )}
          {dayInterviews.length > 0 && (
            <div className="day-interviews">
              {dayInterviews.slice(0, 2).map((interview, idx) => (
                <button
                  type="button"
                  key={idx}
                  className="interview-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedInterview(interview);
                    setShowInterviewModal(true);
                  }}
                >
                  <div className="interview-card-header">
                    <span className={`interview-type-badge ${normalizeInterviewType(interview)}`}>
                      {normalizeInterviewType(interview) === 'internship' ? 'Intern' : 'Job'}
                    </span>
                    <span className="interview-time">
                      {getDisplayTime(interview)}
                    </span>
                  </div>
                  <div className="interview-card-body">
                    <span className="interview-candidate-name">{interview.applicant_name}</span>
                    <span className="interview-job-title">{interview.job_title}</span>
                  </div>
                  <div className="interview-card-footer">
                    <span className="interview-mode">{interview.interview_mode}</span>
                  </div>
                </button>
              ))}
              {dayInterviews.length > 2 && (
                <div className="more-interviews">+{dayInterviews.length - 2} more</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="calendar-week-matrix">
        {headers}
        {days}
      </div>
    );
  };

  const summaryStats = useMemo(() => buildCalendarStats(activeInterviews), [activeInterviews]);

  const renderSidebarCards = () => {
    // Employers see only the main calendar. Jobseekers and interns both use the
    // Upcoming Interviews card; for interns we render it inline instead of a sidebar.
    if (isEmployerView) {
      return null;
    }

    return (
      <>
        <div className="card">
          <div className="card-header">
            <h3>
              <i className="fas fa-clock"></i>
              Upcoming Interviews
            </h3>
          </div>
          <div className="upcoming-interviews-list">
            {getUpcomingInterviews().length > 0 ? (
              getUpcomingInterviews().map((interview, idx) => {
                const interviewDate = interview.interview_local_date || parseDateKey(interview.interview_date);
                const dateDay = interviewDate ? interviewDate.getDate() : '--';
                const dateMonth = interviewDate ? interviewDate.toLocaleDateString('en-US', { month: 'short' }) : '';
                return (
                  <div 
                    key={idx}
                    className="upcoming-interview-item"
                    onClick={() => {
                      setSelectedInterview(interview);
                      setShowInterviewModal(true);
                    }}
                  >
                    <div className="interview-date-badge">
                      <div className="date-day">
                        {dateDay}
                      </div>
                      <div className="date-month">
                        {dateMonth}
                      </div>
                    </div>
                    <div className="interview-details">
                      <div className="interview-candidate">
                        {interview.applicant_name}
                      </div>
                      <div className="interview-job-title">
                        {interview.job_title}
                      </div>
                      <div className="interview-time-mode">
                        <i className="fas fa-clock"></i>
                        {getDisplayTime(interview)}
                        <span className="interview-mode-badge">
                          {interview.interview_mode}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state-small">
                <i className="fas fa-calendar-check"></i>
                <p>No upcoming interviews</p>
                <small>{isCandidateView ? 'Interviews set by employers will appear here' : 'Schedule interviews to see them here'}</small>
              </div>
            )}
          </div>
        </div>

        {!isCandidateView && (
          <div className="stats-inline">
            <div className="stat-item-inline">
              <span className="stat-inline-label">Total Scheduled</span>
              <span className="stat-inline-value">{activeInterviews.length}</span>
            </div>
            <div className="stat-item-inline">
              <span className="stat-inline-label">This Month</span>
              <span className="stat-inline-value">{filteredInterviews.length}</span>
            </div>
            <div className="stat-item-inline">
              <span className="stat-inline-label">Upcoming</span>
              <span className="stat-inline-value">{getUpcomingInterviews().length}</span>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderCalendarMain = () => (
    <div className="calendar-main">
      <div className="calendar-controls">
        <button 
          className="btn-icon"
          onClick={() => navigateMonth(-1)}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        
        <h2 className="calendar-month-title">
          {currentDate.toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
          })}
        </h2>
        
        <button 
          className="btn-icon"
          onClick={() => navigateMonth(1)}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
        
        <button 
          type="button"
          className="btn btn-secondary calendar-today-btn btn-custom-size"
          onClick={goToToday}
        >
          Today
        </button>
      </div>

      <div className="calendar-wrapper">
        {renderCalendar()}
      </div>
    </div>
  );

  if (loading) {
    return (
      <ThemedLoadingSpinner 
        theme="recruiter"
        size="large"
        text="Loading interview calendar..."
        fullScreen={false}
      />
    );
  }

  return (
    <div className={`interview-calendar-container${isEmployerView ? ' employer-view' : ''}`}>
      <div className="calendar-header-section">
        {!isEmployerView && (
          <div className="calendar-title-section">
            <h1>
              <i className="fas fa-calendar-alt"></i>
              Interview Calendar
            </h1>
            <p className="calendar-subtitle">
              {isCandidateView ? 'View your scheduled interviews set by employers' : 'Manage and schedule interviews with candidates'}
            </p>
          </div>
        )}
        <div className="calendar-actions">
          <div className="calendar-quick-actions">
            {shouldUseCustomDropdown ? (
              <EmployerFilterDropdown
                showCounts={false}
                value={interviewTypeFilter}
                onChange={setInterviewTypeFilter}
                options={INTERVIEW_TYPE_FILTER_OPTIONS}
              />
            ) : (
            <select
              value={interviewTypeFilter}
              onChange={(e) => setInterviewTypeFilter(e.target.value)}
              className="calendar-quick-filter"
            >
              {INTERVIEW_TYPE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            )}
            {!isCandidateView && (
            <button 
              type="button"
              className="calendar-quick-schedule"
              onClick={() => setShowScheduleModal(true)}
            >
              <i className="fas fa-plus"></i>
              <span>Schedule</span>
            </button>
            )}
          </div>
        {(isEmployerView || isInternView || isCandidateView) && (
          <div className="calendar-stat-badges">
            {isEmployerView ? (
              <>
                <div className="calendar-stat-card">
                  <div className="calendar-stat-icon applications">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="calendar-stat-meta">
                    <span className="calendar-stat-label">Applications</span>
                    <span className="calendar-stat-value">{applications.length}</span>
                    <span className="calendar-stat-change positive">↑ 0%</span>
                  </div>
                </div>
                <div className="calendar-stat-card">
                  <div className="calendar-stat-icon review">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="calendar-stat-meta">
                    <span className="calendar-stat-label">Pending Review</span>
                    <span className="calendar-stat-value">
                      {applications.filter(app => ['pending', 'reviewed'].includes((app.status || '').toLowerCase())).length}
                    </span>
                    <span className="calendar-stat-change positive">↑ 0%</span>
                  </div>
                </div>
                <div className="calendar-stat-card">
                  <div className="calendar-stat-icon scheduled">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="calendar-stat-meta">
                    <span className="calendar-stat-label">Scheduled</span>
                    <span className="calendar-stat-value">{summaryStats.totalScheduled}</span>
                    <span className="calendar-stat-change positive">↑ 0%</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="calendar-stat-card">
                  <div className="calendar-stat-icon scheduled">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="calendar-stat-meta">
                    <span className="calendar-stat-label">Total Scheduled</span>
                    <span className="calendar-stat-value">{summaryStats.totalScheduled}</span>
                  </div>
                </div>
                <div className="calendar-stat-card">
                  <div className="calendar-stat-icon review">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="calendar-stat-meta">
                    <span className="calendar-stat-label">This Month</span>
                    <span className="calendar-stat-value">{summaryStats.thisMonth}</span>
                  </div>
                </div>
                <div className="calendar-stat-card">
                  <div className="calendar-stat-icon applications">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="calendar-stat-meta">
                    <span className="calendar-stat-label">Upcoming</span>
                    <span className="calendar-stat-value">{summaryStats.upcoming}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        </div>
      </div>

      {isEmployerView ? (
        <div className="calendar-layout calendar-layout-employer">
          {renderCalendarMain()}
        </div>
      ) : isInternView ? (
        <div className="calendar-layout calendar-layout-employer">
          {renderCalendarMain()}
          <div style={{ marginTop: '24px' }}>
            {renderSidebarCards()}
          </div>
        </div>
      ) : (
        <div className="calendar-layout">
          {renderCalendarMain()}
          <div className="calendar-sidebar">
            {renderSidebarCards()}
          </div>
        </div>
      )}

      {/* Interview Details Modal */}
      {showInterviewModal && selectedInterview && (
        <div className="modal-overlay" onClick={() => {
          setShowInterviewModal(false);
          setIsEditMode(false);
        }}>
          <div className="modal-content interview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-calendar-check"></i>
                {isEditMode ? 'Edit Interview' : 'Interview Details'}
              </h2>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowInterviewModal(false);
                  setIsEditMode(false);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {!isEditMode ? (
              <>
            <div className="modal-body">
              {!isCandidateView && (
              <div className="interview-info-section">
                <h3>Candidate Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name</label>
                    <p>{selectedInterview.applicant_name}</p>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <p>{selectedInterview.applicant_email}</p>
                  </div>
                  {selectedInterview.applicant_phone && (
                    <div className="info-item">
                      <label>Phone</label>
                      <p>{selectedInterview.applicant_phone}</p>
                    </div>
                  )}
                </div>
              </div>
              )}

              <div className="interview-info-section">
                <h3>{isCandidateView ? 'Position & Company' : 'Job Details'}</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Position</label>
                    <p>{selectedInterview.job_title}</p>
                  </div>
                  <div className="info-item">
                    <label>Company</label>
                    <p>{selectedInterview.company_name}</p>
                  </div>
                </div>
              </div>

              <div className="interview-info-section">
                <h3>Interview Schedule</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Date</label>
                    <p>{formatDate(selectedInterview.interview_date)}</p>
                  </div>
                  <div className="info-item">
                    <label>Time</label>
                    <p>{getDisplayTime(selectedInterview)}</p>
                  </div>
                  <div className="info-item">
                    <label>Mode</label>
                    <p className="interview-mode-badge">
                      {selectedInterview.interview_mode}
                    </p>
                  </div>
                  {selectedInterview.interview_link && (
                    <div className="info-item full-width">
                      <label>Meeting Link</label>
                      <a 
                        href={selectedInterview.interview_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="interview-link"
                      >
                        {selectedInterview.interview_link}
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    </div>
                  )}
                  {selectedInterview.interview_location && (
                    <div className="info-item full-width">
                      <label>Location</label>
                      <p>{selectedInterview.interview_location}</p>
                    </div>
                  )}
                  {selectedInterview.interview_notes && (
                    <div className="info-item full-width">
                      <label>Notes</label>
                      <p>{selectedInterview.interview_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {isEmployerView && !isCandidateView && (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    initializeEditForm(selectedInterview);
                    setIsEditMode(true);
                  }}
                >
                  <i className="fas fa-edit"></i>
                  Edit
                </button>
              )}
              {isEmployerView && !isCandidateView && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleDeleteInterview}
                >
                  <i className="fas fa-trash"></i>
                  Delete
                </button>
              )}
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setShowInterviewModal(false);
                  setIsEditMode(false);
                }}
              >
                Close
              </button>
            </div>
            </>
            ) : (
              <form onSubmit={handleEditInterview}>
                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Interview Date *</label>
                      <input
                        type="date"
                        value={editForm.interview_date}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          interview_date: e.target.value
                        })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Start Time *</label>
                      <input
                        type="time"
                        value={editForm.interview_start_time}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          interview_start_time: e.target.value
                        })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        value={editForm.interview_end_time}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          interview_end_time: e.target.value
                        })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Interview Mode *</label>
                    {renderInterviewModeSelect(editForm.interview_mode, (nextValue) => setEditForm({
                      ...editForm,
                      interview_mode: nextValue
                    }))}
                  </div>

                  {editForm.interview_mode === 'online' && (
                    <div className="form-group">
                      <label>Meeting Link *</label>
                      <input
                        type="url"
                        value={editForm.interview_link}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          interview_link: e.target.value
                        })}
                        placeholder="https://zoom.us/j/..."
                      />
                    </div>
                  )}

                  {editForm.interview_mode === 'in-person' && (
                    <div className="form-group">
                      <label>Location</label>
                      <input
                        type="text"
                        value={editForm.interview_location}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          interview_location: e.target.value
                        })}
                        placeholder="Office address..."
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={editForm.interview_notes}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        interview_notes: e.target.value
                      })}
                      placeholder="Additional interview details or instructions..."
                      rows="3"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsEditMode(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="btn btn-primary"
                  >
                    <i className="fas fa-save"></i>
                    Save Changes
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal-content schedule-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: calendlySettings.useCalendly ? '900px' : '600px' }}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-calendar-plus"></i>
                Schedule Interview
              </h2>
              <button 
                className="btn-close"
                onClick={() => setShowScheduleModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {calendlySettings.useCalendly && calendlySettings.calendlyUrl ? (
              <div className="modal-body" style={{ padding: '2rem' }}>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label>Select Application *</label>
                  {renderApplicationSelect(true)}
                </div>
                
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  padding: '1rem',
                  minHeight: '600px',
                  background: '#fafafa'
                }}>
                  <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                    The candidate will book a time slot through your Calendly calendar. Once they book, the interview will be automatically scheduled.
                  </p>
                  {calendlyScriptLoaded && scheduleForm.applicant_id ? (
                    <div 
                      className="calendly-inline-widget" 
                      data-url={calendlySettings.calendlyUrl}
                      style={{ minWidth: '320px', height: '600px' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      {!scheduleForm.applicant_id ? (
                        <p>Please select an application above to view the Calendly calendar.</p>
                      ) : (
                        <p>Loading Calendly...</p>
                      )}
                    </div>
                  )}
                </div>
                
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                    <strong>Note:</strong> After the candidate books through Calendly, you'll need to confirm the interview details in the system.
                  </p>
                </div>
                
                <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowScheduleModal(false);
                      resetScheduleForm();
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleScheduleInterview}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Select Application *</label>
                  {renderApplicationSelect(true)}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Interview Date *</label>
                    <input
                      type="date"
                      value={scheduleForm.interview_date}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm,
                        interview_date: e.target.value
                      })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Start Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.interview_start_time}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm,
                        interview_start_time: e.target.value
                      })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={scheduleForm.interview_end_time}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm,
                        interview_end_time: e.target.value
                      })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Interview Mode *</label>
                  {renderInterviewModeSelect(scheduleForm.interview_mode, (nextValue) => setScheduleForm({
                    ...scheduleForm,
                    interview_mode: nextValue
                  }))}
                </div>

                {scheduleForm.interview_mode === 'online' && (
                  <div className="form-group">
                    <label>Meeting Link *</label>
                    <input
                      type="url"
                      value={scheduleForm.interview_link}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm,
                        interview_link: e.target.value
                      })}
                      placeholder="https://zoom.us/j/..."
                      required
                    />
                  </div>
                )}

                {scheduleForm.interview_mode === 'in-person' && (
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={scheduleForm.interview_location}
                      onChange={(e) => setScheduleForm({
                        ...scheduleForm,
                        interview_location: e.target.value
                      })}
                      placeholder="Office address..."
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={scheduleForm.interview_notes}
                    onChange={(e) => setScheduleForm({
                      ...scheduleForm,
                      interview_notes: e.target.value
                    })}
                    placeholder="Additional interview details or instructions..."
                    rows="3"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowScheduleModal(false);
                    resetScheduleForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <i className="fas fa-calendar-check"></i>
                  Schedule Interview
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewCalendar;

